import * as CL from '@design-system/component-library';
import React, { useState } from 'react';
import { CommonErrorType } from '../../common/enums.js';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import { useSubmitReview } from '../../common/hooks/useSubmitReview';
import { fieldCantBeEmptyMsg, t } from '../../common/i18n/index.js';
import { PostReviewRequest } from '../../generated/api/models.js';
import { ConfirmationDialog } from '../ConfirmationDialog/index.js';
import { ValidationMessage } from '../ValidationMessage/ValidationMessage';
import type { CommonError } from '../../common/types/errors.js';
import type { CustomerOrder, OnlineOrderReview } from '../../generated/api/models.js';

import './ReviewOnlineOrderDialog.scss';

interface ReviewOnlineOrderDialogProps {
  approve: boolean;
  onlineOrderReview: OnlineOrderReview;
  customerOrder: CustomerOrder;
  errors?: CommonError[];
  isLoading?: boolean;
  ordererEmail?: string;
  isEoe: boolean;

  onCloseDialog: () => void;
}

export const ReviewOnlineOrderDialog = ({
  onlineOrderReview,
  customerOrder,
  isLoading,
  approve,
  onCloseDialog,
  errors,
  ordererEmail,
  isEoe,
}: ReviewOnlineOrderDialogProps) => {
  const { submitReview } = useSubmitReview(isEoe);
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  const [rejectReason, setRejectReason] = useState<string>();
  const [rejectReasonRequiredError, setRejectReasonRequiredError] = useState<boolean>(false);

  const systemErrors = errors ? errors.filter(err => err.type === CommonErrorType.SYSTEM) : undefined;

  return (
    <ConfirmationDialog
      header={approve ? t.BO8J('Confirm approval for the order') : t.PRH3('Reject a device agreement')}
      body={
        approve ? (
          <div>
            <p>
              {t.PW2I('Please make sure that the order and billing information is correct')}
              <br />
              <br />
              {t.JBMJ('Once the order has been confirmed, the order cannot be cancelled')}
            </p>
          </div>
        ) : (
          <div>
            <div>
              {t.RYLC('We will send notice of the rejection of the Device agreement to')}
              <div>
                {ordererEmail ? (
                  <CL.Link className="review-online-order-dialog--link" linkHref={'mailto:' + ordererEmail}>
                    {ordererEmail}
                  </CL.Link>
                ) : (
                  customerOrder.customerOrderContact
                )}
              </div>
            </div>
            <div>
              <div className="review-online-order-dialog--reject-reason">
                <CL.Input
                  type="textarea"
                  name="rejectReason"
                  label={t.LT6X('Type your comments here..')}
                  maxlength={255}
                  onValueChange={event => {
                    event &&
                      setRejectReason(
                        (event as React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>).target
                          .value
                      );
                    setRejectReasonRequiredError(false);
                  }}
                />
              </div>
              {rejectReasonRequiredError && <ValidationMessage message={t.VPVR(fieldCantBeEmptyMsg)} />}
            </div>
          </div>
        )
      }
      isLoading={isLoading}
      onCloseDialog={onCloseDialog}
      onConfirm={() => {
        if (!rejectReason) {
          setRejectReasonRequiredError(true);
          return;
        } else {
          void submitReview(
            onlineOrderReview,
            PostReviewRequest.ReviewTypeEnum.ONLINE_ORDER,
            approve,
            mdmId,
            rejectReason
          );
        }
      }}
      disableConfirm={approve && onlineOrderReview.billingAccountId === undefined}
      errors={systemErrors}
    />
  );
};
